import { useEffect, useRef, useState } from "react";
import {
  listSkills, listPipelines, type SkillDescriptor,
  driveFindFolders, driveListFiles, driveStage,
  type DriveFolder, type DriveFile,
} from "../api";
import { buildCanonicalPipeline, type FelixGraphLite } from "../pipeline";
import { uploadToNexus, runOnNexus, type FelixArtifact, type FelixNodeResult } from "../felix";
import { glyphOf, stepOf, topoNodes } from "../steps";
import { GridLoader, Pill, type Tone } from "./ai/Primitives";
import { IconDownload } from "./icons";

/**
 * PipelineView — docked in the right-column stack. Pick a document/report and
 * generate it on the hosted Nexus runner. The input control adapts to the
 * pipeline:
 *   - declares an `input_schema` → a parameter form (data pipelines, e.g. vigeo)
 *   - has an input node, no schema → file upload (document pipelines, aiwd/jrr)
 *   - neither → just generate.
 */

type RunStatus = Record<string, "running" | "done" | "error">;

function prettyLabel(key: string): string {
  return key.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function PipelineView({ instance }: { instance: string }) {
  const [skills, setSkills] = useState<SkillDescriptor[]>([]);
  const [declared, setDeclared] = useState<FelixGraphLite[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<RunStatus>({});
  const [artifacts, setArtifacts] = useState<FelixArtifact[]>([]);
  const [runLog, setRunLog] = useState<string[]>([]);
  const [params, setParams] = useState<Record<string, string>>({});
  const [driveEnabled, setDriveEnabled] = useState(false);
  const [driveOpen, setDriveOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true); setErr(null); setRunStatus({}); setArtifacts([]); setRunLog([]); setParams({}); setDriveOpen(false);
    Promise.all([
      listPipelines(instance).then((d) => { setDriveEnabled(Boolean(d.drive_enabled)); return (d.pipelines as FelixGraphLite[]) || []; }).catch(() => { setDriveEnabled(false); return []; }),
      listSkills(instance).then((d) => d.skills).catch(() => [] as SkillDescriptor[]),
    ]).then(([pls, sks]) => {
      setDeclared(pls);
      setSkills(sks);
      setSelected((s) => s ?? (pls[0]?.id ?? sks[0]?.id ?? null));
    }).finally(() => setLoading(false));
  }, [instance]);

  // When the instance declares document pipelines, the picker lists THOSE.
  // Otherwise it lists the available tools.
  const usePipelines = declared.length > 0;
  const graph: FelixGraphLite | null = usePipelines
    ? (declared.find((p) => p.id === selected) ?? declared[0] ?? null)
    : (selected ? buildCanonicalPipeline(selected, instance) : null);
  const isWired = usePipelines;

  // Input mode. A declared input_schema → a parameter form; else an input node
  // means uploaded files; else nothing to provide.
  const schemaProps = graph?.input_schema?.properties;
  const paramKeys = schemaProps ? Object.keys(schemaProps) : [];
  const requiredKeys = graph?.input_schema?.required || [];
  const isParamInput = paramKeys.length > 0;
  const isFileInput = !isParamInput && !!graph?.nodes.some((n) => n.kind === "input");
  const missingRequired = requiredKeys.some((k) => !(params[k] || "").trim());

  const options = usePipelines
    ? declared.map((p) => ({ id: p.id, label: p.name.replace(/^AIWD — /, "") }))
    : skills.map((s) => ({ id: s.id, label: s.id }));

  const resetRun = () => { setErr(null); setRunStatus({}); setArtifacts([]); setRunLog([]); };
  const pickPipeline = (id: string) => { setSelected(id); setParams({}); setDriveOpen(false); resetRun(); };

  const stepName = (id: string): string => {
    const n = graph?.nodes.find((x) => x.id === id);
    return n ? stepOf(n.kind, n.label) : "step";
  };

  const buildParams = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const k of paramKeys) {
      const v = (params[k] || "").trim();
      if (!v) continue;
      out[k] = schemaProps?.[k]?.type === "array" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v;
    }
    return out;
  };

  const run = async (opts: { files?: FileList | null; driveKeys?: string[]; params?: Record<string, unknown> }) => {
    if (!graph) return;
    setBusy("run"); setDriveOpen(false); resetRun();
    try {
      const g: FelixGraphLite = JSON.parse(JSON.stringify(graph));
      if (opts.files && opts.files.length) {
        const up = await uploadToNexus(instance, Array.from(opts.files));
        for (const n of g.nodes) if (n.kind === "input") n.config = { ...n.config, files: up.map((f) => f.key) };
        setRunLog((l) => [...l, `uploaded ${up.length} document(s)`]);
      }
      if (opts.driveKeys && opts.driveKeys.length) {
        for (const n of g.nodes) if (n.kind === "input") n.config = { ...n.config, files: opts.driveKeys };
        setRunLog((l) => [...l, `staged ${opts.driveKeys!.length} file(s) from Drive`]);
      }
      if (opts.params) {
        // Merge the form values into the input node's config — node_input passes
        // non-file config through downstream (felix_run's `inputs` mechanism).
        for (const n of g.nodes) if (n.kind === "input") n.config = { ...n.config, ...opts.params };
      }
      const found: FelixArtifact[] = [];
      // Both the live progress poll and the end-of-run buffered replay report
      // node status, so the log dedupes per node while artifacts (replay-only)
      // and status chips (idempotent sets) apply from either source.
      const logged = new Set<string>();
      await runOnNexus(instance, g, {
        onNodeStart: (id) => { setRunStatus((s) => ({ ...s, [id]: "running" })); },
        onNodeDone: (id, r: FelixNodeResult) => {
          setRunStatus((s) => ({ ...s, [id]: "done" }));
          if (!logged.has(id)) { logged.add(id); setRunLog((l) => [...l, `✓ ${stepName(id)}`]); }
          // The render and output nodes both report the document: keep one per URL.
          for (const a of r.artifacts || []) {
            if (!found.some((f) => (f.url || f.token) === (a.url || a.token))) found.push(a);
          }
          if (r.artifacts?.length) setArtifacts([...found]);
        },
        onNodeError: (id, e) => {
          setRunStatus((s) => ({ ...s, [id]: "error" }));
          // The live poll carries no message (status chip only); the replay does.
          if (e) setRunLog((l) => [...l, `✗ ${stepName(id)}: ${e}`]);
        },
        onRunDone: (status) => { setRunLog((l) => [...l, status === "done" ? "✓ ready" : `finished: ${status}`]); },
      });
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="pipeline-docked">
      <label className="pipeline-pick">
        <span>{usePipelines ? "Report" : "Tool"}</span>
        <select value={selected ?? ""} disabled={loading || !options.length}
                onChange={(e) => pickPipeline(e.target.value)}>
          {!options.length && <option value="">{loading ? "Loading…" : "Nothing here yet"}</option>}
          {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </label>

      {graph ? (
        <>
          <PipelineDiagram graph={graph} status={runStatus} />

          {isParamInput && (
            <div className="pipeline-params">
              {paramKeys.map((k) => {
                const spec = schemaProps![k];
                const req = requiredKeys.includes(k);
                const isDate = k === "from" || k === "to" || /\bdate\b/i.test(spec.description || "");
                return (
                  <label key={k} className="pipeline-param">
                    <span>{prettyLabel(k)}{req && <em> *</em>}</span>
                    <input
                      type={isDate ? "date" : "text"}
                      value={params[k] || ""}
                      placeholder={spec.description || ""}
                      onChange={(e) => setParams((p) => ({ ...p, [k]: e.target.value }))}
                    />
                  </label>
                );
              })}
            </div>
          )}

          <div className="pipeline-actions">
            {isFileInput ? (
              <>
                <input ref={fileInput} type="file" multiple style={{ display: "none" }}
                       onChange={(e) => run({ files: e.target.files })} />
                <button className="primary" disabled={busy !== null} onClick={() => fileInput.current?.click()}>
                  {busy === "run" ? "Generating…" : "Upload and generate"}
                </button>
                {driveEnabled && (
                  <button className="secondary" disabled={busy !== null}
                          onClick={() => setDriveOpen((o) => !o)}>
                    {driveOpen ? "Close Drive" : "Choose from Drive"}
                  </button>
                )}
              </>
            ) : (
              <>
                <button className="primary" disabled={busy !== null || (isParamInput && missingRequired)}
                        onClick={() => run({ params: isParamInput ? buildParams() : undefined })}>
                  {busy === "run" ? "Generating…" : "Generate"}
                </button>
                <span className="pipeline-run-hint">
                  {isParamInput ? "Fill in the details — no upload needed." : "No upload needed."}
                </span>
              </>
            )}
          </div>

          {isFileInput && driveEnabled && driveOpen && (
            <DrivePicker
              instance={instance}
              busy={busy !== null}
              onGenerate={(keys) => run({ driveKeys: keys })}
            />
          )}

          {!!artifacts.length && (
            <div className="pipeline-artifacts">
              <div className="grill-spec-list-label">Your documents</div>
              {artifacts.map((a) => (
                <a key={a.url || a.token} href={a.url} download className="pipeline-artifact">
                  <IconDownload size={13} />
                  <span className="pipeline-artifact-name">{a.name}</span>
                  {Number.isFinite(a.bytes) && <span>{Math.round(a.bytes / 1024)} KB</span>}
                </a>
              ))}
            </div>
          )}
          {!!runLog.length && (
            <div className="pipeline-runlog">{runLog.map((l, i) => (
              <div key={i} className={l.startsWith("✗") ? "err" : l.startsWith("✓") ? "ok" : ""}>{l}</div>
            ))}</div>
          )}
          {err && <div className="error-banner">{err}</div>}
          {!isWired && <div className="pipeline-note">Not set up yet.</div>}
        </>
      ) : (
        <div className="pipeline-empty">
          {loading ? <GridLoader label="Loading" /> : "No report selected."}
        </div>
      )}
    </div>
  );
}

/**
 * DrivePicker — pull a client's engagement files from the connected Google Drive
 * instead of uploading. Two steps: find the client folder by name, then choose
 * files from it. Selected files are staged to R2 and handed to the pipeline run
 * exactly like an upload.
 */
function DrivePicker({
  instance, busy, onGenerate,
}: { instance: string; busy: boolean; onGenerate: (keys: string[]) => void }) {
  const [q, setQ] = useState("");
  const [folders, setFolders] = useState<DriveFolder[] | null>(null);
  const [folder, setFolder] = useState<DriveFolder | null>(null);
  const [files, setFiles] = useState<DriveFile[] | null>(null);
  const [skippedFolders, setSkippedFolders] = useState<string[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [pBusy, setPBusy] = useState<null | "search" | "list" | "stage">(null);
  const [pErr, setPErr] = useState<string | null>(null);

  const search = async () => {
    if (!q.trim()) return;
    setPBusy("search"); setPErr(null); setFolder(null); setFiles(null);
    try {
      const r = await driveFindFolders(instance, q.trim());
      setFolders(r.folders);
    } catch (e) { setPErr(String((e as Error).message || e)); }
    finally { setPBusy(null); }
  };

  const openFolder = async (f: DriveFolder) => {
    setFolder(f); setPBusy("list"); setPErr(null); setFiles(null);
    try {
      const r = await driveListFiles(instance, f.id);
      setFiles(r.files);
      setSkippedFolders(r.skipped_folders || []);
      setChecked(defaultSelection(r.files));
    } catch (e) { setPErr(String((e as Error).message || e)); }
    finally { setPBusy(null); }
  };

  const toggle = (id: string) =>
    setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const stageAndGenerate = async () => {
    const ids = files!.filter((f) => checked.has(f.id)).map((f) => f.id);
    if (!ids.length) return;
    setPBusy("stage"); setPErr(null);
    try {
      const r = await driveStage(instance, ids);
      if (!r.staged.length) { setPErr("nothing staged" + (r.skipped[0] ? `: ${r.skipped[0].reason}` : "")); return; }
      onGenerate(r.staged.map((s) => s.key));  // parent runs the pipeline
    } catch (e) { setPErr(String((e as Error).message || e)); }
    finally { setPBusy(null); }
  };

  return (
    <div className="drive-picker">
      <div className="drive-picker-search">
        <input
          type="text" value={q} placeholder="client name (e.g. Speed Power)"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
        />
        <button className="secondary" disabled={pBusy !== null || !q.trim()} onClick={search}>
          {pBusy === "search" ? "…" : "Find"}
        </button>
      </div>

      {folders && !folder && (
        folders.length ? (
          <div className="drive-picker-folders">
            {folders.map((f) => (
              <button key={f.id} className="drive-folder-row" onClick={() => openFolder(f)}>
                📁 {f.name}
              </button>
            ))}
          </div>
        ) : <div className="drive-picker-empty">No client folder matches “{q}”.</div>
      )}

      {folder && (
        <div className="drive-picker-files">
          <div className="drive-picker-crumb">
            <button className="linkish" onClick={() => { setFolder(null); setFiles(null); }}>← folders</button>
            <span>📁 {folder.name}</span>
          </div>
          {pBusy === "list" ? (
            <div className="drive-picker-empty">Reading folder…</div>
          ) : files && files.length ? (
            <>
              {files.map((f) => (
                <label key={f.id} className="drive-file-row">
                  <input type="checkbox" checked={checked.has(f.id)} onChange={() => toggle(f.id)} />
                  <span className="drive-file-name">{f.name}</span>
                  <span className="drive-file-meta">{Math.round(f.size / 1024)}KB</span>
                </label>
              ))}
              <button className="primary drive-stage-btn"
                      disabled={busy || pBusy !== null || checked.size === 0 || checked.size > MAX_STAGE_FILES}
                      onClick={stageAndGenerate}>
                {pBusy === "stage" ? "Staging…"
                  : checked.size > MAX_STAGE_FILES ? `Select up to ${MAX_STAGE_FILES} files`
                  : `▶ Generate from ${checked.size} file${checked.size === 1 ? "" : "s"}`}
              </button>
            </>
          ) : (
            <div className="drive-picker-empty">
              No eligible files in this folder{skippedFolders.length ? ` (${skippedFolders.length} subfolder(s) skipped)` : ""}.
            </div>
          )}
        </div>
      )}

      {pErr && <div className="error-banner">{pErr}</div>}
    </div>
  );
}

/** The Worker stages at most this many Drive files per request. */
const MAX_STAGE_FILES = 50;

/** The files ticked when a client folder opens: one copy of each document.
 *  A client folder repeats a file across its subfolders; the newest copy of
 *  each name and size is ticked, the rest start unticked. */
export function defaultSelection(files: DriveFile[]): Set<string> {
  const newest = new Map<string, DriveFile>();
  for (const f of files) {
    const key = `${f.name}\u0000${f.size}`;
    const prev = newest.get(key);
    if (!prev || (f.modifiedTime || "") > (prev.modifiedTime || "")) newest.set(key, f);
  }
  return new Set([...newest.values()].map((f) => f.id));
}

/** A node's run state reads by colour before it reads by word. */
const NODE_TONE: Record<string, Tone> = {
  running: "running", done: "done", error: "error",
};
const NODE_LABEL: Record<string, string> = {
  running: "Running", done: "Done", error: "Failed",
};

function PipelineDiagram({ graph, status }: { graph: FelixGraphLite; status: RunStatus }) {
  // The template node feeds the Generate step; for a clean linear view we show
  // only the sequential steps (the chosen document is named in the header),
  // in execution order so the chain lights up left to right as the run proceeds.
  const chain = topoNodes(graph.nodes, graph.edges).filter((n) => n.kind !== "template");
  return (
    <div className="pipeline-diagram">
      <div className="pipeline-chain">
        {chain.map((n, i) => (
          <div key={n.id} className="pipeline-node-wrap">
            <div className={`pipeline-node kind-${n.kind} run-${status[n.id] || "idle"}`}>
              <span className={`pipeline-node-glyph tone-${NODE_TONE[status[n.id]] || "neutral"}`}>
                {glyphOf(n.kind)}
              </span>
              <span className="pipeline-node-label">{stepOf(n.kind, n.label)}</span>
              {status[n.id] && (
                <Pill tone={NODE_TONE[status[n.id]] || "neutral"} dot={status[n.id] === "running"}>
                  {NODE_LABEL[status[n.id]] || status[n.id]}
                </Pill>
              )}
            </div>
            {i < chain.length - 1 && <span className="pipeline-arrow">→</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
